import React from "react";
import { Users, Smartphone, Monitor, Tablet, TrendingUp } from "lucide-react";
import DateRangeFilter from "./DateRangeFilter";

interface UserTrackingStatsProps {
  stats: any;
  isLoading: boolean;
  selectedDays?: number;
  onDaysChange?: (days: number) => void;
  onNewRegistrationsClick?: () => void;
  onAvgSessionClick?: () => void;
}

const UserTrackingStats: React.FC<UserTrackingStatsProps> = ({
  stats,
  isLoading,
  selectedDays = 30,
  onDaysChange,
  onNewRegistrationsClick,
  onAvgSessionClick,
}) => {
  const statCards = [
    {
      title: "Total Users",
      value: stats?.totalUsers || 0,
      icon: Users,
      color: "blue",
      description: "Total registered users (all time)",
    },
    {
      title: "New Registrations",
      value: stats?.registeredInPeriod || 0,
      icon: Users,
      color: "indigo",
      description: `Users registered in last ${selectedDays} day${
        selectedDays > 1 ? "s" : ""
      }`,
      onClick: onNewRegistrationsClick,
    },
    {
      title: "Active Users",
      value: stats?.activeToday || 0,
      icon: TrendingUp,
      color: "green",
      description: `Users active in last ${selectedDays} day${
        selectedDays > 1 ? "s" : ""
      }`,
    },
    {
      title: "Mobile Users",
      value: stats?.mobileUsers || 0,
      icon: Smartphone,
      color: "purple",
      description: `Mobile users active in last ${selectedDays} day${
        selectedDays > 1 ? "s" : ""
      }`,
    },
    {
      title: "Desktop Users",
      value: stats?.desktopUsers || 0,
      icon: Monitor,
      color: "orange",
      description: `Desktop users active in last ${selectedDays} day${
        selectedDays > 1 ? "s" : ""
      }`,
    },
    {
      title: "Tablet Users",
      value: stats?.tabletUsers || 0,
      icon: Tablet,
      color: "pink",
      description: `Tablet users active in last ${selectedDays} day${
        selectedDays > 1 ? "s" : ""
      }`,
    },
    {
      title: "Avg Session Time",
      value: stats?.avgSessionTime || 0,
      icon: TrendingUp,
      color: "indigo",
      description: "Average session duration (minutes)",
      suffix: " min",
      onClick: onAvgSessionClick,
    },
  ];

  const getColorClasses = (color: string) => {
    switch (color) {
      case "blue":
        return {
          bg: "bg-blue-50",
          text: "text-blue-600",
          iconBg: "bg-blue-100",
          border: "border-blue-200",
        };
      case "green":
        return {
          bg: "bg-green-50",
          text: "text-green-600",
          iconBg: "bg-green-100",
          border: "border-green-200",
        };
      case "purple":
        return {
          bg: "bg-purple-50",
          text: "text-purple-600",
          iconBg: "bg-purple-100",
          border: "border-purple-200",
        };
      case "orange":
        return {
          bg: "bg-orange-50",
          text: "text-orange-600",
          iconBg: "bg-orange-100",
          border: "border-orange-200",
        };
      case "pink":
        return {
          bg: "bg-pink-50",
          text: "text-pink-600",
          iconBg: "bg-pink-100",
          border: "border-pink-200",
        };
      case "indigo":
        return {
          bg: "bg-indigo-50",
          text: "text-indigo-600",
          iconBg: "bg-indigo-100",
          border: "border-indigo-200",
        };
      default:
        return {
          bg: "bg-gray-50",
          text: "text-gray-600",
          iconBg: "bg-gray-100",
          border: "border-gray-200",
        };
    }
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className="bg-gray-100 rounded-lg p-6 animate-pulse">
            <div className="flex items-center justify-between">
              <div>
                <div className="h-4 bg-gray-300 rounded w-24 mb-2"></div>
                <div className="h-8 bg-gray-300 rounded w-16"></div>
              </div>
              <div className="h-12 w-12 bg-gray-300 rounded-lg"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      {/* Date Range Filter */}
      {onDaysChange && (
        <div className="mb-6 flex justify-end">
          <DateRangeFilter
            selectedDays={selectedDays}
            onDaysChange={onDaysChange}
            isLoading={isLoading}
          />
        </div>
      )}

      {/* Summary Information */}
      {stats?.summary && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-blue-800">
                Data Summary for {stats.summary.periodDescription}
              </h3>
              <p className="text-sm text-blue-600 mt-1">
                <strong>Activity:</strong> {stats.summary.activeInPeriod} out of{" "}
                {stats.summary.totalRegistered} total users were active (
                {stats.summary.activePercentage}% activity rate)
                <br />
                <strong>Registrations:</strong>{" "}
                {stats.summary.registeredInPeriod} new users registered (
                {stats.summary.registrationPercentage}% of total users)
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-800">
                {stats.summary.activePercentage}%
              </div>
              <div className="text-xs text-blue-600">Activity Rate</div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
      {statCards.map((stat, index) => {
        const IconComponent = stat.icon;
        const colors = getColorClasses(stat.color);

        return (
          <div
            key={index}
            className={`${colors.bg} ${colors.border} rounded-lg p-6 border ${
              stat.onClick ? "cursor-pointer hover:shadow-md" : ""
            }`}
            onClick={stat.onClick}
          >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">
                    {stat.title}
                  </p>
                  <p className={`text-2xl font-bold ${colors.text}`}>
                    {stat.value.toLocaleString()}
                    {stat.suffix || ""}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {stat.description}
                  </p>
                </div>
                <div className={`${colors.iconBg} p-3 rounded-lg`}>
                  <IconComponent className={`h-6 w-6 ${colors.text}`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UserTrackingStats;
